import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { BlockSlotConflict } from '@/scheduling/services/use-save-block-slot';
import { formatBlockerRow } from '@/shared/lib/blocker-row';

/**
 * BlockersConflictDialog — Block (presentational AlertDialog)
 *
 * Server returns `blockers[]` on `POST /api/Scheduling/BlockSlot` when
 * existing appointments overlap the requested slot; the block is NOT
 * saved in that case. This dialog lists the conflicting appointments
 * so the operator can go reschedule / cancel them and retry.
 *
 * Flutter parity: copy verbatim from
 * `block_availability_widget.dart` blockers-state handler. Title
 * + footer copy + the "Please, reschedule..." line are exact.
 *
 * Extracted from `block-availability-panel.tsx` (PROD-824 gap close).
 * Pure presentational — `blockers` drives `open` (non-null shows the
 * dialog), `onClose` clears state in the parent. Lets Storybook
 * snapshot this dialog directly without mocking the mutation that
 * would otherwise need to fire to populate `blockers`.
 *
 * One dialog for both actors: the provider route (`BlockSlot`) and the device
 * route (`BlockResourceSlot`) share `BlockSlotCore` and its blocker projection
 * server-side, and this panel renders the same component for both. Each
 * blocker's `start` is clinic-local wall clock with NO zone suffix; the row is
 * formatted by `formatBlockerRow` (`@/shared/lib/blocker-row`), which reads it
 * textually. See that module for the contract (PROD-2527).
 */

interface Props {
  /** When non-null, the AlertDialog opens with these rows rendered.
   *  Pass `null` to close. */
  blockers: BlockSlotConflict[] | null;
  /** Fires when the AlertDialog dismisses (OK button OR backdrop). */
  onClose: () => void;
}

export function BlockersConflictDialog({ blockers, onClose }: Props) {
  return (
    <AlertDialog
      open={blockers !== null}
      onOpenChange={(open) => !open && onClose()}
    >
      <AlertDialogContent className="sm:max-w-[var(--dialog-width-confirm)]">
        <AlertDialogHeader>
          <AlertDialogTitle>
            Blocking availability failed due to following blockers
          </AlertDialogTitle>
        </AlertDialogHeader>
        <div className="space-y-2 max-h-[280px] overflow-y-auto scrollbar-hide">
          {blockers?.map((b, i) => (
            <div
              key={b.id ?? `blocker-${i}`}
              className="rounded-card border border-[var(--surface-line)] bg-grey-100 px-3 py-2 [font:var(--type-body)] text-[var(--ink-body)]"
            >
              {formatBlockerRow(b)}
            </div>
          ))}
        </div>
        <AlertDialogDescription className="[font:var(--type-body)] text-[var(--ink-secondary)]">
          Please, reschedule or cancel the above appointments to proceed.
        </AlertDialogDescription>
        <AlertDialogFooter>
          <AlertDialogAction onClick={onClose}>OK</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
